import { useMemo, useState } from "react";
import { trpc } from "../lib/trpc";
import { useTranslation } from "../i18n";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Package as PackageIcon } from "lucide-react";
import { DEFAULT_CURRENCY } from "@shared/const";

type PackageItem = {
  type: "digital" | "print" | "video";
  quantity: number;
  printFormatId?: number;
};

export function PackagesManager() {
  const { t } = useTranslation();
  const tt = {
    title: t?.packages?.title || "Pakete",
    description: t?.packages?.description || "Verwalten Sie Ihre Pakete",
    createPackage: t?.packages?.createPackage || "Paket erstellen",
    packageCreated: t?.packages?.packageCreated || "Paket erstellt",
    packageUpdated: t?.packages?.packageUpdated || "Paket aktualisiert",
    packageDeleted: t?.packages?.packageDeleted || "Paket gelöscht",
    deletePackageConfirm: t?.packages?.deletePackageConfirm || "Paket wirklich löschen?",
    noPackages: t?.packages?.noPackages || "Keine Pakete vorhanden",
    createFirstPackage: t?.packages?.createFirstPackage || "Erstellen Sie Ihr erstes Paket",
    packageName: t?.packages?.packageName || "Paketname",
    packageDescription: t?.packages?.packageDescription || "Paketbeschreibung",
    packagePrice: t?.packages?.packagePrice || "Paketpreis",
    isActive: t?.packages?.isActive || "Aktiv",
    items: t?.packages?.items || "Elemente",
    addItem: t?.packages?.addItem || "Element hinzufügen",
    itemType: t?.packages?.itemType || "Typ",
    itemQuantity: t?.packages?.itemQuantity || "Menge",
    digital: t?.packages?.digital || "Digital",
    print: t?.packages?.print || "Print",
    video: t?.packages?.video || "Video",
    digitalImages: t?.packages?.digitalImages || "{count} digitale Bilder",
    videos: t?.packages?.videos || "{count} Videos",
    printFormat: t?.packages?.printFormat || "Format",
    physicalPrints: t?.packages?.physicalPrints || "{count} Abzüge ({format})",
    editPackage: t?.packages?.editPackage || "Paket bearbeiten",
    printFormatRequired: t?.packages?.printFormatRequired || "Bitte ein Print-Format auswählen.",
  };
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPackageId, setEditingPackageId] = useState<number | null>(null);
  const [packageName, setPackageName] = useState("");
  const [packageDescription, setPackageDescription] = useState("");
  const [packagePrice, setPackagePrice] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [items, setItems] = useState<PackageItem[]>([]);

  const { data: packages, refetch } = trpc.packages.list.useQuery();
  const { data: printFormats } = trpc.printFormats.list.useQuery();
  const { data: settings } = trpc.settings.get.useQuery();
  const currency = settings?.currency || DEFAULT_CURRENCY;
  const formatCurrency = useMemo(
    () => (value: string | number) => {
      const numeric = typeof value === "string" ? parseFloat(value || "0") : value;
      return `${numeric.toFixed(2)} ${currency}`;
    },
    [currency]
  );

  const createMutation = trpc.packages.create.useMutation({
    onSuccess: () => {
      toast.success(tt.packageCreated);
      refetch();
      closeDialog();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const updateMutation = trpc.packages.update.useMutation({
    onSuccess: () => {
      toast.success(tt.packageUpdated);
      refetch();
      closeDialog();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteMutation = trpc.packages.delete.useMutation({
    onSuccess: () => {
      toast.success(tt.packageDeleted);
      refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const openCreateDialog = () => {
    setEditingPackageId(null);
    setPackageName("");
    setPackageDescription("");
    setPackagePrice("");
    setIsActive(true);
    setItems([]);
    setIsDialogOpen(true);
  };

  const openEditDialog = (pkg: any) => {
    setEditingPackageId(pkg.id);
    setPackageName(pkg.name);
    setPackageDescription(pkg.description || "");
    setPackagePrice(pkg.price);
    setIsActive(pkg.isActive);
    setItems(
      pkg.items.map((item: any) => ({
        type: item.type,
        quantity: item.quantity,
        printFormatId: item.printFormatId ?? undefined,
      }))
    );
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingPackageId(null);
  };

  const handleSave = () => {
    if (!packageName || !packagePrice) {
      toast.error(t?.validation?.required || "Pflichtfeld fehlt");
      return;
    }

    const missingPrintFormat = items.some(
      (item) => item.type === "print" && (!item.printFormatId || Number.isNaN(item.printFormatId))
    );
    if (missingPrintFormat) {
      toast.error(tt.printFormatRequired);
      return;
    }

    const sanitizedItems = items.map((item) => ({
      type: item.type,
      quantity: Number(item.quantity) > 0 ? Number(item.quantity) : 1,
      printFormatId:
        item.type === "print" && item.printFormatId && !Number.isNaN(item.printFormatId)
          ? Number(item.printFormatId)
          : undefined,
    }));

    const data = {
      name: packageName,
      description: packageDescription,
      price: packagePrice,
      isActive,
      items: sanitizedItems,
    };

    if (editingPackageId) {
      updateMutation.mutate({ ...data, id: editingPackageId });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleDelete = (id: number) => {
    if (confirm(tt.deletePackageConfirm)) {
      deleteMutation.mutate({ id });
    }
  };

  const addItem = () => {
    setItems([...items, { type: "digital", quantity: 1 }]);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, updates: Partial<PackageItem>) => {
    setItems(items.map((item, i) => (i === index ? { ...item, ...updates } : item)));
  };

  const getItemDescription = (item: any) => {
    if (item.type === "digital") {
      return tt.digitalImages.replace("{count}", item.quantity.toString());
    } else if (item.type === "video") {
      return tt.videos.replace("{count}", item.quantity.toString());
    } else if (item.type === "print") {
      const format = item.printFormat ? `${item.printFormat.name}` : tt.printFormat;
      return tt.physicalPrints
        .replace("{count}", item.quantity.toString())
        .replace("{format}", format);
    }
    return "";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{tt.title}</CardTitle>
            <CardDescription>{tt.description}</CardDescription>
          </div>
          <Button onClick={openCreateDialog}>
            <Plus className="w-4 h-4 mr-2" />
            {tt.createPackage}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!packages || packages.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <PackageIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{tt.noPackages}</p>
            <p className="text-sm">{tt.createFirstPackage}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {packages.map((pkg: any) => (
              <Card key={pkg.id}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold text-lg">{pkg.name}</h3>
                        {!pkg.isActive && (
                          <span className="text-xs bg-muted px-2 py-1 rounded">
                            Inactive
                          </span>
                        )}
                      </div>
                      {pkg.description && (
                        <p className="text-sm text-muted-foreground mb-3">
                          {pkg.description}
                        </p>
                      )}
                      <div className="space-y-1 mb-3">
                        {pkg.items.map((item: any, idx: number) => (
                          <div key={idx} className="text-sm">
                            • {getItemDescription(item)}
                          </div>
                        ))}
                      </div>
                      <p className="font-bold text-lg">
                        {formatCurrency(pkg.price)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => openEditDialog(pkg)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDelete(pkg.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPackageId ? tt.editPackage : tt.createPackage}
              </DialogTitle>
              <DialogDescription>{tt.description}</DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>{tt.packageName}</Label>
                <Input
                  value={packageName}
                  onChange={(e) => setPackageName(e.target.value)}
                  placeholder={tt.packageName}
                />
              </div>

              <div>
                <Label>{tt.packageDescription}</Label>
                <Textarea
                  value={packageDescription}
                  onChange={(e) => setPackageDescription(e.target.value)}
                  placeholder={tt.packageDescription}
                  rows={3}
                />
              </div>

              <div>
                <Label>{tt.packagePrice}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={packagePrice}
                  onChange={(e) => setPackagePrice(e.target.value)}
                  placeholder="10.00"
                />
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={isActive} onCheckedChange={setIsActive} />
                <Label>{tt.isActive}</Label>
              </div>

              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label>{tt.items}</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    {tt.addItem}
                  </Button>
                </div>

                <div className="space-y-3">
                  {items.map((item, index) => (
                    <Card key={index}>
                      <CardContent className="pt-4">
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                          <div className="col-span-1 md:col-span-4">
                            <Label className="text-xs">{tt.itemType}</Label>
                            <Select
                              value={item.type}
                              onValueChange={(value: any) =>
                                updateItem(index, {
                                  type: value,
                                  ...(value !== "print" ? { printFormatId: undefined } : {}),
                                })
                              }
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="digital">{tt.digital}</SelectItem>
                                <SelectItem value="print">{tt.print}</SelectItem>
                                <SelectItem value="video">{tt.video}</SelectItem>
                              </SelectContent>
                            </Select>
                            </div>

                          <div className="col-span-1 md:col-span-3">
                            <Label className="text-xs">{tt.itemQuantity}</Label>
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => {
                                const next = parseInt(e.target.value, 10);
                                updateItem(index, {
                                  quantity: Number.isNaN(next) || next < 1 ? 1 : next,
                                });
                              }}
                            />
                          </div>

                          {item.type === "print" && (
                            <div className="col-span-1 md:col-span-4">
                              <Label className="text-xs">
                                {tt.printFormat}
                              </Label>
                              <Select
                                value={item.printFormatId ? item.printFormatId.toString() : ""}
                                onValueChange={(value) =>
                                  updateItem(index, {
                                    printFormatId: value ? parseInt(value, 10) : undefined,
                                  })
                                }
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder={tt.printFormat} />
                                </SelectTrigger>
                                <SelectContent>
                                  {printFormats?.map((format: any) => (
                                    <SelectItem
                                      key={format.id}
                                      value={format.id.toString()}
                                    >
                                      {format.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}

                          <div className="col-span-1 flex items-end md:col-span-1">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(index)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={closeDialog}>
                {t.common.cancel}
              </Button>
              <Button
                onClick={handleSave}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {t.common.save}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
